import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { faArrowToTop } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { toast } from "~/ui";
import Menu from "~/ui/menu";

import { type AbstractAction, ActionButton } from "./shared";

import type { CarryForward_note$key } from "~/__generated__/CarryForward_note.graphql";
import type { CarryForwardSuggestedActionMutation } from "~/__generated__/CarryForwardSuggestedActionMutation.graphql";

import styles from "./CarryForward.module.css";

const ACTION_ID = "carry-forward" as const;

const noteFragment = graphql`
	fragment CarryForward_note on Note {
		id
		actionItemsCarriedForward
		talkingPointsCarriedForward
		stream {
			carryActionItems
			carryTalkingPoints
		}
		previousNote {
			pendingAgendaCount
			pendingTodoCount
		}
	}
`;

const carryEverythingForwardMutation = graphql`
	mutation CarryForwardSuggestedActionMutation($noteId: ID!, $type: String!) {
		noteCarryForwardBlocks(noteId: $noteId, type: $type) {
			note {
				actionItemsCarriedForward
				talkingPointsCarriedForward
			}
		}
	}
`;

async function carryItemsForward(noteId: string, type: "action-item" | "talking-point" | "all") {
	const { noteCarryForwardBlocks } = await commitMutationPromise<CarryForwardSuggestedActionMutation>(environment, {
		mutation: carryEverythingForwardMutation,
		variables: {
			noteId,
			type,
		},
	});

	if (!noteCarryForwardBlocks) {
		toast.error("Could not carry forward your action items and talking points.", { title: "Something went wrong" });
		Sentry.captureException(new Error("Failed to carry forward action items and talking points"), {
			extra: { noteId },
		});
	}
}

export function useCarryItemsForward(
	noteKey: CarryForward_note$key,
): [carryItemsForward: () => void, carryForwardCount: number] {
	const note = useFragment(noteFragment, noteKey);

	const talkingPointsAvailable: number =
		note.stream.carryTalkingPoints === null /* i.e. "ask me" */ &&
		!note.talkingPointsCarriedForward &&
		!!note.previousNote?.pendingAgendaCount
			? note.previousNote.pendingAgendaCount
			: 0;
	const actionItemsAvailable: number =
		note.stream.carryActionItems === null /* i.e. "ask me" */ &&
		!note.actionItemsCarriedForward &&
		!!note.previousNote?.pendingTodoCount
			? note.previousNote.pendingTodoCount
			: 0;

	const carryForwardType =
		actionItemsAvailable && talkingPointsAvailable ? "all" : actionItemsAvailable ? "action-item" : "talking-point";

	const doCarryForward = useCallback(() => {
		carryItemsForward(note.id, carryForwardType);
	}, [carryForwardType, note.id]);

	return [doCarryForward, talkingPointsAvailable + actionItemsAvailable];
}

export interface CarryForwardProps {
	note: CarryForward_note$key;
}

function Button({ note: noteKey }: CarryForwardProps) {
	const [carryItemsForward, carryForwardCount] = useCarryItemsForward(noteKey);

	return (
		<ActionButton actionId={ACTION_ID} onClick={carryItemsForward}>
			<FontAwesomeIcon icon={faArrowToTop} className={styles.buttonIcon} size="sm" />
			Carry forward
			<span className={styles.count}>{carryForwardCount}</span>
		</ActionButton>
	);
}

function MenuItem({ note: noteKey }: CarryForwardProps) {
	const [carryItemsForward, carryForwardCount] = useCarryItemsForward(noteKey);

	return (
		<Menu.Item icon={faArrowToTop} onSelect={carryItemsForward}>
			Carry forward
			<span className={styles.count}>{carryForwardCount}</span>
		</Menu.Item>
	);
}

export default {
	id: ACTION_ID,
	button: Button,
	menuItem: MenuItem,
} satisfies AbstractAction;
